import React, { useEffect } from 'react';
import {
  Avatar,
  Button,
  ColumnWithPlugins,
  CopyEmailButton,
  HTMLParser,
  Popover,
  RowWithPlugins,
  SvgIcon,
  Table,
} from '@gik/ui';
import { useBemCN } from '@gik/core/utils/bemBlock';
import { renderSupportGivenIcon, Timeline } from '../Timeline';
import { Breakpoint, useBreakpoint } from '@gik/core/hooks/CustomHooks';
import { ISupporter, SupportGivenType } from '@gik/core/models/gik/ISupporter';
import { Row } from 'react-table';
import { downloadSupporters } from '@gik/api/crm/supporters';
import { useInkindStore } from '@gik/inkind/store/InkindStore';
import { PopoverMenu } from '@gik/ui/PopoverMenu';
import { translationKeys } from '../../i18n/en';
import { translationKeys as commonTranslationKeys } from '@gik/i18n/en/common';
import { useTranslation } from 'react-i18next';
import { openPremiumPageUpgradeModal, openShareInkindModal } from '@gik/inkind-page';
import { useInkind } from '@gik/api/inkinds/inkind';
import { getAssetsUrl } from '@gik/core/utils/LinkUtils';
import { Analytics } from '@gik/core/utils';

export interface ISupportersProps {
  supporters: ISupporter[];
}

export const Supporters = ({ supporters }: ISupportersProps) => {
  const bem = useBemCN('supporters');
  const isMdUp = useBreakpoint(Breakpoint.MD);
  const { t } = useTranslation();

  const inkindRouteId = useInkindStore(state => state.inkindRouteId);
  const { data: inkind } = useInkind(inkindRouteId);

  const inkindPageIsLoaded = !!inkind;
  const inkindPageIsPremium = inkind?.isPremium;

  // if the user does not have this premium feature then blur out this page and show a premium subscription modal
  React.useEffect(() => {
    if (inkindPageIsLoaded && !inkindPageIsPremium) {
      // show premium subscription modal after 1 second
      setTimeout(() => {
        openPremiumPageUpgradeModal({
          initiatedOn: 'supportersPage',
          variant: 'viewSupporters',
          inkindRouteId,
        });
      }, 1000);
    }
  }, [inkindRouteId, inkindPageIsLoaded, inkindPageIsPremium]);

  const [exportPopoverIsOpen, setExportPopoverIsOpen] = React.useState<boolean>();
  const exportPopoverIsOpenRef = React.useRef<boolean>();
  exportPopoverIsOpenRef.current = exportPopoverIsOpen;

  const commonColumnProps: Partial<ColumnWithPlugins<ISupporter>> = {
    minWidth: 20,
    width: 50,
    maxWidth: 200,
    sortType: 'basic',
  };

  const columns: ColumnWithPlugins<ISupporter>[] = [
    {
      ...(commonColumnProps as ColumnWithPlugins<ISupporter>),
      Header: 'Supporter',
      accessor: 'fullName',
      Cell: ({ row }) => {
        return (
          <div {...bem('supporter-cell')}>
            <Avatar imageUrl={row.original.avatarUrl} />
            <div {...bem('supporter-content')}>
              <span {...bem('supporter-name')}>{row.original.fullName}</span>
              {row.original.isOrganizer && <span {...bem('page-organizer')}>{t(translationKeys.pageOrganizer)}</span>}
            </div>
          </div>
        );
      },
    },
  ];

  const expandColumn: ColumnWithPlugins<ISupporter> = {
    id: 'expander', // Make sure it has an ID
    width: '70px',
    Header: ({ getToggleAllRowsExpandedProps }) => <span {...getToggleAllRowsExpandedProps()}>Support Given</span>,
    Cell: ({ row: _row }) => {
      const row = _row as RowWithPlugins<ISupporter>;
      // Use the row.canExpand and row.getToggleRowExpandedProps prop getter
      // to build the toggle for expanding a row
      const isExpanded = row.isExpanded;
      const supporter = row.original;
      useEffect(() => {
        if (isExpanded) {
          Analytics.fireEvent(
            'SupporterDetailsViewed',
            {
              isOrganizer: supporter.isOrganizer.toString(),
              supportGiven: supporter.supportGiven.join(', '),
            },
            () => ['supportGiven'],
            keys => keys.filter(key => key !== 'supportGiven')
          );
        }
      }, [isExpanded, supporter]);

      const expandedProps = row.getToggleRowExpandedProps();
      // TODO: fix typing
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      delete (expandedProps as any).title;

      const detailsIcon = <SvgIcon name="InformationCircleOutline" className="gik-table__expanded-icon" />;

      return (
        <span {...bem('details-wrapper')} {...expandedProps}>
          <div {...bem('details-hitbox')} />

          {row.original.supportGiven.length > 0
            ? row.original.supportGiven?.map((item: string, index: number) => {
                return (
                  <div key={`${item}-${index}`} {...bem('help-icon')}>
                    {renderSupportGivenIcon(item as SupportGivenType)}
                  </div>
                );
              })
            : detailsIcon}

          {/* TODO move this span with gik class into Table component if this needs top be more reusable */}
          <span className="gik-table__expand-toggle">
            {row.isExpanded ? <SvgIcon size="xs" name="ChevronUp" /> : <SvgIcon size="xs" name="ChevronDown" />}
          </span>
        </span>
      );
    },
  };
  columns.push(expandColumn);

  const renderRowSubComponent = React.useCallback(
    ({ row }) => (
      <div {...bem('history')}>
        <div {...bem('email')}>
          <SvgIcon name="Mail" {...bem('email-icon')} />
          <a {...bem('email-link')} href={`mailto:${row.original.emailAddress}`}>
            {row.original.emailAddress}
          </a>
          <CopyEmailButton
            pill={false}
            circle
            size="sm"
            iconSize="base"
            text=""
            tooltip="Copy Email"
            variant="white"
            textToCopy={row.original.emailAddress}
          />
        </div>

        {row.original.history?.length ? (
          <Timeline data={row.original.history} />
        ) : (
          <div {...bem('empty-support')}>{t(translationKeys.noSupportYet)}</div>
        )}
      </div>
    ),
    []
  );

  function handleExport(rows: Row<ISupporter>[], filter?: string) {
    let emails: string[];
    if (filter) {
      emails = rows.map(item => item.original.emailAddress);
    }

    Analytics.fireEvent(
      'SupportersExported',
      {
        numSupporters: rows.length.toString(),
        filterApplied: (!!filter).toString(),
      },
      () => ['numSupporters'],
      keys => keys.filter(key => key !== 'numSupporters')
    );

    downloadSupporters(inkindRouteId, emails);
  }
  return (
    <Table
      {...bem()}
      variant="plain"
      data={supporters}
      columns={columns}
      hasFlexLayout
      hasExpandableRows
      hasPagination
      hasGlobalFilter
      globalFilterCollapse={!isMdUp}
      globalFilterPlaceholder="Search Supporters"
      renderRowSubComponent={renderRowSubComponent}
      customEmptyFilterView={({ state: { globalFilter } }) => (
        <div {...bem('empty-filter')}>{t(translationKeys.noResultsForFilter, { filter: globalFilter })} </div>
      )}
      customEmptyView={() => (
        <div {...bem('empty')}>
          <img src={`${getAssetsUrl()}crm/img/Supporters.png`} {...bem('empty-image')} />
          <span {...bem('empty-title')}>{t(translationKeys.emptyTitle)}</span>
          <br />
          <p>
            <HTMLParser rawHtml={t(translationKeys.emptyDescription)} />
          </p>
          <Button
            block
            append={<SvgIcon set="gik-icons" name="ShareAlt" size="xl" />}
            {...bem('empty-share')}
            onClick={() =>
              openShareInkindModal(
                { isOrganizer: true, pageShortUrl: inkind.shortUrl, pageTitle: inkind.title },
                'supportersPage',
                inkind.url
              )
            }
          >
            {t(commonTranslationKeys.share)}
          </Button>
        </div>
      )}
      customDetailsDisplay={props => {
        const pos = props.state.pageIndex * props.state.pageSize;
        return (
          <span>
            {t(translationKeys.paginationDetails, {
              current: `${1 + pos}-${pos + props.state.pageSize}`,
              total: props.preGlobalFilteredRows.length,
            })}
          </span>
        );
      }}
      customToolbar={({ rows, state: { globalFilter } }) => {
        const handleExportClick = () => {
          if (globalFilter) {
            setExportPopoverIsOpen(true);
            return;
          }

          handleExport(rows, globalFilter);
        };

        const exportButton = (
          <Button
            {...bem('btn-export')}
            pill
            variant="white-outline"
            prepend={<SvgIcon name="DownloadOutline" />}
            onClick={handleExportClick}
          >
            {t(commonTranslationKeys.export)}
          </Button>
        );

        return (
          <div>
            {!globalFilter ? (
              exportButton
            ) : (
              <Popover
                noPad
                placement="bottom-start"
                trigger={exportButton}
                isOpen={exportPopoverIsOpenRef.current}
                onClose={() => setExportPopoverIsOpen(false)}
              >
                <PopoverMenu>
                  <PopoverMenu.Item onClick={() => handleExport(rows, globalFilter)}>
                    {t(translationKeys.exportFiltered)}
                  </PopoverMenu.Item>
                  <PopoverMenu.Item onClick={() => handleExport(rows)}>{t(translationKeys.exportAll)}</PopoverMenu.Item>
                </PopoverMenu>
              </Popover>
            )}
          </div>
        );
      }}
      fullWidth
      truncate
    />
  );
};
